import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type CellStatus = "none" | "submitted" | "reviewed";

export interface ChallengeColumn { focusAreaId: string; name: string; title: string; number: string; assignmentId: string | null; max: number }
export interface ChallengeCell { status: CellStatus; submissionId: string | null; score: number | null }

/** Challenge columns plus a per-application map of submission status and area score. */
export function useTopShopChallengeMatrix(programId?: string) {
  return useQuery({
    queryKey: ["admin", "top-shop", "challenge-matrix", programId],
    enabled: !!programId,
    queryFn: async () => {
      const { data: areas, error } = await supabase.from("program_focus_areas")
        .select("id, name, sort_order, module_id").eq("program_id", programId!).eq("is_active", true).order("sort_order");
      if (error) throw error;
      const moduleIds = (areas ?? []).map((a) => a.module_id).filter(Boolean) as string[];
      const areaIds = (areas ?? []).map((a) => a.id);

      const [mods, asg, crit, apps] = await Promise.all([
        moduleIds.length ? supabase.from("modules").select("id, title").in("id", moduleIds) : Promise.resolve({ data: [], error: null }),
        moduleIds.length ? supabase.from("assignments").select("id, module_id").in("module_id", moduleIds).order("created_at") : Promise.resolve({ data: [], error: null }),
        areaIds.length ? supabase.from("program_challenge_criteria").select("id, focus_area_id, max_points").in("focus_area_id", areaIds).eq("is_active", true) : Promise.resolve({ data: [], error: null }),
        supabase.from("program_applications").select("id, user_id").eq("program_id", programId!),
      ]);
      for (const r of [mods, asg, crit, apps]) if (r.error) throw r.error;

      const modTitle = new Map(((mods.data ?? []) as any[]).map((m) => [m.id, m.title as string]));
      const asgByModule = new Map<string, string>();
      ((asg.data ?? []) as any[]).forEach((a) => { if (!asgByModule.has(a.module_id)) asgByModule.set(a.module_id, a.id); });
      const critArea = new Map(((crit.data ?? []) as any[]).map((c) => [c.id, c.focus_area_id as string]));

      const columns: ChallengeColumn[] = (areas ?? []).map((a, i) => ({
        focusAreaId: a.id,
        name: a.name,
        title: (a.module_id && modTitle.get(a.module_id)) || a.name,
        number: String(i + 1).padStart(2, "0"),
        assignmentId: a.module_id ? asgByModule.get(a.module_id) ?? null : null,
        max: ((crit.data ?? []) as any[]).filter((c) => c.focus_area_id === a.id).reduce((s, c) => s + c.max_points, 0),
      }));

      const appList = (apps.data ?? []) as { id: string; user_id: string }[];
      const assignmentIds = columns.map((c) => c.assignmentId).filter(Boolean) as string[];
      const userIds = appList.map((a) => a.user_id);
      const appIds = appList.map((a) => a.id);

      const [subs, scores] = await Promise.all([
        assignmentIds.length && userIds.length
          ? supabase.from("assignment_submissions").select("id, user_id, assignment_id, status").in("assignment_id", assignmentIds).in("user_id", userIds)
          : Promise.resolve({ data: [], error: null }),
        appIds.length ? supabase.from("program_criterion_scores").select("application_id, criterion_id, points").in("application_id", appIds) : Promise.resolve({ data: [], error: null }),
      ]);
      if (subs.error) throw subs.error;
      if (scores.error) throw scores.error;

      const subKey = new Map(((subs.data ?? []) as any[]).map((s) => [`${s.user_id}:${s.assignment_id}`, s]));
      const scoreKey = new Map<string, number>();
      ((scores.data ?? []) as any[]).forEach((s) => {
        const area = critArea.get(s.criterion_id);
        if (!area) return; // inactive criterion
        const k = `${s.application_id}:${area}`;
        scoreKey.set(k, (scoreKey.get(k) ?? 0) + Number(s.points));
      });

      const cells: Record<string, Record<string, ChallengeCell>> = {};
      for (const app of appList) {
        cells[app.id] = {};
        for (const col of columns) {
          const sub = col.assignmentId ? subKey.get(`${app.user_id}:${col.assignmentId}`) : undefined;
          const status: CellStatus = !sub ? "none" : sub.status === "passed" || sub.status === "failed" ? "reviewed" : "submitted";
          const k = `${app.id}:${col.focusAreaId}`;
          cells[app.id][col.focusAreaId] = { status, submissionId: sub?.id ?? null, score: scoreKey.has(k) ? scoreKey.get(k)! : null };
        }
      }
      return { columns, cells, activeMax: columns.reduce((s, c) => s + c.max, 0) };
    },
  });
}

export const CELL_LABEL: Record<CellStatus, string> = { none: "Not started", submitted: "Submitted", reviewed: "Reviewed" };
